import {
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  SourcingEventV2Dto,
  useAwardSourcingEventMutation,
  useGetEvaluationQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { pickDefaultWinner } from "./pickDefaultWinner";
import { SupplierCountIndicator } from "./SupplierCountIndicator";

export const SelectWinner = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const { data: evaluation, isLoading, isError } = useGetEvaluationQuery({ eventId: event.id });
  const [award, { isLoading: isAwarding }] = useAwardSourcingEventMutation();

  const [winnerIds, setWinnerIds] = useState<string[]>([]);
  const [internalNote, setInternalNote] = useState("");
  const [currency, setCurrency] = useState<Currency>(event.publicContext.estimatedValue?.currency ?? Currency.Nok);
  const [amount, setAmount] = useState(
    event.publicContext.estimatedValue?.value != null ? String(event.publicContext.estimatedValue.value) : "",
  );

  // Drop any selected bid that disappears from a refetch (e.g. disqualified mid-flow) so the Award
  // button never submits a stale id, and default to the top-ranked bid when nothing is selected.
  useEffect(() => {
    if (!evaluation) return;
    const validIds = evaluation.bids.map((b) => b.bidId);
    setWinnerIds((prev) => {
      const stillValid = prev.filter((id) => validIds.includes(id));
      if (stillValid.length > 0) return stillValid.length === prev.length ? prev : stillValid;
      const fallback = pickDefaultWinner(evaluation.bids);
      return fallback ? [fallback] : [];
    });
  }, [evaluation]);

  const maxParticipants = event.publicContext.frameworkMaxParticipants ?? undefined;

  const toggleWinner = (bidId: string) =>
    setWinnerIds((prev) => (prev.includes(bidId) ? prev.filter((id) => id !== bidId) : [...prev, bidId]));

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError || !evaluation) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load the evaluation.")}</Text>
      </Center>
    );
  }
  if (evaluation.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  const submit = async () => {
    if (winnerIds.length === 0) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await award({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidIds: winnerIds,
          internalNote: internalNote.trim() || null,
          eventValue: parsed != null && !Number.isNaN(parsed) ? { value: parsed, currency } : null,
        },
      }).unwrap();
      toast({ title: t("Event awarded"), status: "success", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Stack spacing="6">
      <AwardDecisionHistory event={event} />

      <Stack spacing="2">
        <AwardBidsTable evaluation={evaluation} mode="select" selectedBidIds={winnerIds} onToggle={toggleWinner} />
        <SupplierCountIndicator selectedCount={winnerIds.length} maxParticipants={maxParticipants} />
      </Stack>

      <FormControl>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder="0"
          />
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} bg="smBackground" maxW="32">
            {Object.values(Currency).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </HStack>
      </FormControl>

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Flex justify="end" align="center" gap="3">
        <Text fontSize="sm" color="smMuted">
          {t("You'll prepare and send award letters to each supplier after awarding.")}
        </Text>
        <Button colorScheme="teal" onClick={submit} isLoading={isAwarding} isDisabled={winnerIds.length === 0}>
          {t("Award to selected suppliers")}
        </Button>
      </Flex>
    </Stack>
  );
};
